import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import Spinner from "../../components/Shared/Spinner";
import { GetIFRSQuestionExhibits } from "../../api/Quiz/GetIFRSQuestionExhibits";
import { GetIFRSQuestionById } from "../../api/Quiz/GetIFRSQuestionById";
import Button from "../../components/Shared/Button";
import { FileText, FileSpreadsheet, Edit } from "lucide-react";
import TrashIcon from "../../components/Icons/TrashIcon";
import DeleteModal from "../../components/Shared/DeleteModal";
import { DeleteIFRSQuestionExhibit } from "../../api/Quiz/DeleteIFRSQuestionExhibit";
import { toast } from "react-toastify";
import AddIcon from "../../components/Icons/AddIcon";
import Modal from "../../components/Shared/Modal";
import Spreadsheet from "react-spreadsheet";
import * as XLSX from "xlsx";
import { useUserDataStore } from "../../stores/user";

const IFRSQuestionExhibits = () => {
  const { userData } = useUserDataStore();
  const userRole = userData?.role;

  const { courseId, exam_id, question_id } = useParams<{
    courseId: string;
    exam_id: string;
    question_id: string;
  }>();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 10;
  const [isSpreadsheetModalOpen, setIsSpreadsheetModalOpen] = useState(false);
  const [isLoadingSpreadsheet, setIsLoadingSpreadsheet] = useState(false);
  const [selectedExhibit, setSelectedExhibit] = useState<{
    title: string;
    exhibitData: Record<string, any[][]>;
  } | null>(null);

  // Fetch question details for title
  const { isLoading: isQuestionLoading } = useQuery({
    queryFn: () => GetIFRSQuestionById(exam_id || "", question_id || ""),
    queryKey: ["ifrs-question", exam_id, question_id],
    enabled: !!exam_id && !!question_id && userRole !== "admin",
    refetchOnWindowFocus: false,
  });

  // Fetch exhibits with pagination
  const { data: ExhibitsResponse, isLoading: isExhibitsLoading } = useQuery({
    queryFn: () =>
      GetIFRSQuestionExhibits(question_id || "", { page, limit, sort: "asc" }),
    queryKey: ["ifrs-question-exhibits", question_id, page, limit],
    enabled: !!question_id,
    staleTime: 0,
    refetchInterval: 0,
    refetchOnWindowFocus: false,
  });

  const exhibits = ExhibitsResponse?.data?.data || [];
  const meta = ExhibitsResponse?.data?.meta;

  const getFileIcon = (type: string) => {
    if (type === "pdf") {
      return <FileText size={20} className="text-red-500" />;
    }
    return <FileSpreadsheet size={20} className="text-green-500" />;
  };

  const fetchAndParseSpreadsheet = async (url: string) => {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const result: Record<string, any[][]> = {};

    workbook.SheetNames.forEach((sheetName) => {
      const sheet = workbook.Sheets[sheetName];
      result[sheetName] = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    });
    return result;
  };

  const handleViewSpreadsheet = async (exhibit: any) => {
    if (exhibit.type === "spreadsheet") {
      setIsSpreadsheetModalOpen(true);

      if (exhibit.exhibitData) {
        setSelectedExhibit({
          title: exhibit.title,
          exhibitData: exhibit.exhibitData,
        });
      } else if (exhibit.filePath) {
        setIsLoadingSpreadsheet(true);
        try {
          const data = await fetchAndParseSpreadsheet(exhibit.filePath);
          setSelectedExhibit({
            title: exhibit.title,
            exhibitData: data,
          });
        } catch (error) {
          console.error("Failed to load spreadsheet:", error);
          toast.error("Failed to load spreadsheet preview");
        } finally {
          setIsLoadingSpreadsheet(false);
        }
      }
    }
  };

  if (isQuestionLoading || isExhibitsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="py-7">
      <div className="flex items-center gap-4 mb-7">
        <Link
          to={`/dashboard/courses/${courseId}/ifrs-exam/${exam_id}/questions`}
          className="font-semibold text-primary hover:text-primary/80"
        >
          ← Back to Questions
        </Link>
        <h1 className="text-burntBlack font-bold text-[32px]">
          Question Exhibits
        </h1>
      </div>

      <div className="p-8 bg-white shadow rounded-xl border-black/15">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Exhibits List</h2>
          <Link to={`create`}>
            <Button
              className="!h-12 w-48 !text-sm font-bold rounded-md !bg-[#4379EE] hover:!bg-primary"
              variant="primary"
            >
              <AddIcon className="mr-1.5" />
              Create Exhibit
            </Button>
          </Link>
        </div>

        {exhibits.length > 0 ? (
          <>
            <div className="overflow-x-auto scrolling-touch scrollbar-thumb-blue scrollbar-thumb-rounded scrollbar-track-blue-lighter scrollbar-w-2">
              <table className="w-full table-auto">
                <thead className="text-lg capitalize">
                  <tr className="bg-[#F1F4F9] rounded-t-lg">
                    <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                      <div className="text-sm text-left">ID</div>
                    </th>
                    <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                      <div className="text-sm text-left">Title</div>
                    </th>
                    <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                      <div className="text-sm text-left">Type</div>
                    </th>
                    <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                      <div className="text-sm text-left">Actions</div>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {exhibits.map((exhibit) => (
                    <tr key={exhibit.id}>
                      <td className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                        <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold">
                          {exhibit.id}
                        </div>
                      </td>
                      <td className="px-2 py-3 first:pl-5 last:pr-5">
                        <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold">
                          {exhibit.title}
                        </div>
                      </td>
                      <td className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                        {exhibit.type === "spreadsheet" ? (
                          <button
                            onClick={() => handleViewSpreadsheet(exhibit)}
                            className="flex items-center gap-2 text-sm cursor-pointer text-primary hover:text-primary/80"
                          >
                            {getFileIcon(exhibit.type)}
                            <span className="capitalize">{exhibit.type}</span>
                          </button>
                        ) : (
                          <a
                            href={exhibit.filePath}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm cursor-pointer text-primary hover:text-primary/80"
                          >
                            {getFileIcon(exhibit.type)}
                            <span className="capitalize">{exhibit.type}</span>
                          </a>
                        )}
                      </td>
                      <td className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                        <div className="flex items-stretch justify-center gap-3">
                          <div className="flex items-center justify-center flex-shrink-0 p-3 text-sm font-semibold border cursor-pointer rounded-xl hover:bg-gray-50">
                            <Link
                              to={`/dashboard/courses/${courseId}/ifrs-exam/${exam_id}/questions/${question_id}/exhibits/edit/${exhibit.id}`}
                            >
                              <Edit size={18} />
                            </Link>
                          </div>
                          <ExhibitDeleteButton
                            questionId={question_id || ""}
                            exhibitId={exhibit.id}
                            onDelete={() => {
                              queryClient.invalidateQueries({
                                queryKey: [
                                  "ifrs-question-exhibits",
                                  question_id,
                                ],
                              });
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {meta && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-600">
                  Showing {exhibits.length} of {meta.total} exhibits
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="gray"
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={!meta.hasPreviousPage || isExhibitsLoading}
                    className="!h-10 !text-sm"
                  >
                    Previous
                  </Button>
                  <div className="flex items-center px-4 text-sm font-semibold">
                    Page {meta.page} of {meta.lastPage}
                  </div>
                  <Button
                    variant="gray"
                    onClick={() => setPage((prev) => prev + 1)}
                    disabled={!meta.hasNextPage || isExhibitsLoading}
                    className="!h-10 !text-sm"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="py-12 text-center">
            <p className="text-gray-500">No exhibits found for this question</p>
          </div>
        )}
      </div>

      {/* Spreadsheet Modal */}
      <Modal
        isOpen={isSpreadsheetModalOpen}
        closeModal={setIsSpreadsheetModalOpen}
        className="!max-w-[90vw] !w-[90vw]"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-burntBlack">
              {selectedExhibit?.title || "Spreadsheet Exhibit"}
            </h2>
            <button
              onClick={() => setIsSpreadsheetModalOpen(false)}
              className="text-xl font-bold text-gray-600 hover:text-gray-800"
            >
              ✕
            </button>
          </div>

          {isLoadingSpreadsheet ? (
            <div className="flex items-center justify-center py-20">
              <Spinner />
            </div>
          ) : (
            selectedExhibit?.exhibitData && (
              <div className="space-y-6">
                {Object.entries(selectedExhibit.exhibitData).map(
                  ([sheetName, sheetData]) => {
                    // Convert the array data to spreadsheet format
                    const spreadsheetData = sheetData.map((row) =>
                      row.map((cell) => ({ value: cell || "", readOnly: true }))
                    );

                    return (
                      <div key={sheetName} className="p-4 border rounded-lg">
                        <h3 className="mb-3 text-lg font-semibold text-burntBlack">
                          {sheetName}
                        </h3>
                        <div className="overflow-auto max-h-[60vh]">
                          <Spreadsheet data={spreadsheetData} />
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            )
          )}
        </div>
      </Modal>
    </div>
  );
};

interface ExhibitDeleteButtonProps {
  questionId: string;
  exhibitId: string;
  onDelete?: () => void;
}

const ExhibitDeleteButton = ({
  questionId,
  exhibitId,
  onDelete,
}: ExhibitDeleteButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const handleDelete = async (id: string) => {
    const result = await DeleteIFRSQuestionExhibit(questionId, id);
    if (!result.error) {
      if (onDelete) {
        onDelete();
      }
      queryClient.invalidateQueries({
        queryKey: ["ifrs-question-exhibits", questionId],
      });
      toast.success("Exhibit deleted successfully");
    } else {
      toast.error("Failed to delete exhibit");
    }
  };

  return (
    <>
      <div className="flex items-center justify-center flex-shrink-0 p-3 text-sm font-semibold border cursor-pointer rounded-xl hover:bg-gray-50">
        <button onClick={() => setIsOpen(true)}>
          <TrashIcon />
        </button>
      </div>
      <DeleteModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        mutationFn={handleDelete}
        itemId={exhibitId}
        queryKey={`ifrs-question-exhibits-${questionId}`}
      />
    </>
  );
};

export default IFRSQuestionExhibits;
